import type { CreateTaskNextSteps, getApiReactHooks } from '@frontend/api-client';
import { skipQuotaStep } from '@frontend/api-client';
import type {
  CreateTaskSource,
  DraftTask,
  DraftTaskDetail,
  TypedFormData as GenericTypedFormData,
  Recurrence,
  Task,
  UpdateDraftTaskMutation,
} from '@frontend/api-types';
import { type CreateRecurrenceRequest, type CreateTaskMutation, type TaskInitialQuestion } from '@frontend/api-types';
import type { getMutationHandler } from '@frontend/duck-tape';
import { dayjs, debounce, deserializeJson, generateUUID, serializeJson } from '@frontend/duck-tape';
import type { getUseAnalyticsHook } from '@frontend/react';
import { useListState } from '@frontend/react';
import type { getSocketClient, SocketMethod } from '@frontend/sockets';
import { useEffect, useState } from 'react';
import type {
  Answer,
  CreateTaskData,
  OnCreateTaskCallbackPayload,
  SkippableCreateTaskStep,
  TaskCreationOptions,
} from './helpers';
import {
  allSteps,
  defaultTaskData,
  formatAiQuestionnaireAnswersForSubmission,
  formatDraftTaskAiQuestionnaire,
  initialTaskCreationState,
  stepsForRepeatingTasksOnly,
} from './helpers';

export type UseGlobalTaskCreationProps = {
  closeModal: () => void;
  dependencies: {
    TypedFormData: typeof GenericTypedFormData;
    analyticsTrack: ReturnType<typeof getUseAnalyticsHook>['track'];
    api: {
      useCreateRecurrenceMutation: ReturnType<typeof getApiReactHooks>['useCreateRecurrenceMutation'];
      useCreateTaskMutation: ReturnType<typeof getApiReactHooks>['useCreateTaskMutation'];
      useLazyRetrieveClientStatusQuery: ReturnType<typeof getApiReactHooks>['useLazyRetrieveClientStatusQuery'];
      useUpdateDraftTaskMutation: ReturnType<typeof getApiReactHooks>['useUpdateDraftTaskMutation'];
    };
    handleMutation: ReturnType<typeof getMutationHandler>;
    socket: ReturnType<typeof getSocketClient>;
  };
  hideIndividualTaskQuotaStep: boolean;
  onSuccessCreateOneOffTask: (data: Task) => void;
  onSuccessCreateRecurringTask: (
    data: Recurrence & { formattedNextStartDate: string; nameOrDefaultName: string },
  ) => void;
  onSuccessUpdateDraftTask: (data: DraftTask) => void;
  openModal: () => void;
  platform: CreateTaskSource;
  token: string;
};

/**
 * @description
 * This hook is used to create tasks (one-off and recurring) and draft tasks. It handles all of the state and logic for
 * task creation but doesn't handle presentation logic.
 */
export const useGlobalTaskCreation = ({
  closeModal,
  dependencies,
  hideIndividualTaskQuotaStep,
  onSuccessCreateOneOffTask,
  onSuccessCreateRecurringTask,
  onSuccessUpdateDraftTask,
  openModal,
  platform,
  token,
}: UseGlobalTaskCreationProps) => {
  const { analyticsTrack, api, handleMutation, socket, TypedFormData } = dependencies;
  const {
    useCreateRecurrenceMutation,
    useCreateTaskMutation,
    useLazyRetrieveClientStatusQuery,
    useUpdateDraftTaskMutation,
  } = api;
  const { disconnectSocket, getSocket, initializeSocket } = socket;
  const [state, setState] = useState(initialTaskCreationState);
  const [onCreateCallback, setOnCreateCallback] = useState<(data?: OnCreateTaskCallbackPayload) => void>();
  const [initialQuestions, { setState: setInitialQuestions }] = useListState<TaskInitialQuestion>([]);
  const [answers, { replace: onUpdateAnswer, setState: setAnswers }] = useListState<Answer>([]);
  const [descriptionSnapshot, setDescriptionSnapshot] = useState('');
  const [shouldSkipTitleGeneration, setShouldSkipTitleGeneration] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [createRecurrence, { isLoading: isCreateRecurrenceLoading }] = useCreateRecurrenceMutation();
  const [createTask, { isLoading: isCreateTaskLoading }] = useCreateTaskMutation();
  const [retrieveClientStatus, { data: clientStatus, isUninitialized }] = useLazyRetrieveClientStatusQuery();
  const [updateDraftTask, { isLoading: isUpdateDraftTaskLoading }] = useUpdateDraftTaskMutation();
  const [draftTask, setDraftTask] = useState<DraftTaskDetail | null>(null);

  useEffect(() => {
    setAnswers((answers) =>
      initialQuestions.map(({ choices, question, ...rest }) => {
        const answer = answers.find((a) => a.question?.includes(question))?.answer;
        return {
          ...rest,
          answer: !answer ? (choices?.length ? [] : '') : answer,
          choices: choices?.map((choice) => ({ id: choice, value: choice })) ?? [],
          question,
        };
      }),
    );
  }, [initialQuestions]);

  const updateTaskData = (data: Partial<CreateTaskData>) => {
    setState((prev) => ({ ...prev, taskData: { ...prev.taskData, ...data } }));
  };

  const clearCreateTaskState = () => {
    disconnectSocket();
    analyticsTrack({
      data: {
        currentStep: state.currentStep,
        uniqueKey: state.uniqueKey,
        ...(draftTask && { entityId: draftTask.id, entityType: 'draft' }),
        ...(clientStatus && {
          currentActiveTasks: clientStatus.currentActiveTasks,
          individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
          planMaxActiveTasks: clientStatus.planMaxActiveTasks,
        }),
      },
      event: 'taskFlowEnded',
      topic: 'tasks',
    });
    setState(initialTaskCreationState);
    setInitialQuestions([]);
    setAnswers([]);
    setDescriptionSnapshot('');
    setDraftTask(null);
    setOnCreateCallback(undefined);
  };

  const handleCreateRecurringTask = async (data: CreateTaskData) => {
    const {
      buffer = 0,
      calendarRecurrenceId,
      calendarRecurrenceTaskSuggestionIdx,
      description,
      exampleId,
      exampleType,
      interval,
      intervalCount,
      letterRecommendationId,
      letterRecommendationTaskSuggestionIdx,
      name,
      nextStartDate,
      nextSteps,
      researchThreadId,
      researchThreadReferenceData,
      sharedWithClientIds,
    } = data;

    const startDate = dayjs(nextStartDate).subtract(buffer, 'days').format('YYYY-MM-DD');
    const formattedNextStartDate = dayjs(nextStartDate).format('MM/DD/YYYY');
    const nameOrDefaultName = name || `Repeating Task for ${formattedNextStartDate}`;
    disconnectSocket();
    return handleMutation({
      mutation: async () => {
        analyticsTrack({
          data: {
            description,
            exampleId,
            exampleType,
            letterRecommendationId,
            letterRecommendationTaskSuggestionIdx,
            name: nameOrDefaultName,
            nextSteps,
            uniqueKey: state.uniqueKey,
            ...(clientStatus && {
              currentActiveTasks: clientStatus.currentActiveTasks,
              individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
              planMaxActiveTasks: clientStatus.planMaxActiveTasks,
            }),
          },
          event: 'createRecurrenceSubmitButtonSelected',
          topic: 'recurrences',
        });

        const basePayload: CreateRecurrenceRequest['input'] = {
          ai_questionnaire: answers.length
            ? { responses: formatAiQuestionnaireAnswersForSubmission(answers) }
            : undefined,
          buffer,
          calendar_recurrence: calendarRecurrenceId,
          calendar_recurrence_task_suggestion_idx: calendarRecurrenceTaskSuggestionIdx,
          description,
          interval,
          interval_count: intervalCount,
          name: nameOrDefaultName,
          proceed_decision: nextSteps!,
          shared_with: sharedWithClientIds,
          start_date: startDate as DateISOString,
        };

        const data = {
          ...basePayload,
          ...(researchThreadId
            ? {
                search_thread: {
                  id: researchThreadId,
                  message_idx: researchThreadReferenceData?.messageIdx,
                  task_suggestion_idx: researchThreadReferenceData?.taskSuggestionIdx,
                },
              }
            : {}),
          ...(letterRecommendationId && letterRecommendationTaskSuggestionIdx !== undefined
            ? {
                letter_recommendation: {
                  id: letterRecommendationId,
                  task_suggestion_idx: letterRecommendationTaskSuggestionIdx,
                },
              }
            : {}),
        };

        return createRecurrence(data).unwrap();
      },
      onSuccess: (successData) => {
        const { id: recurrenceId } = successData;
        onSuccessCreateRecurringTask({ ...successData, formattedNextStartDate, nameOrDefaultName });
        analyticsTrack({
          data: {
            exampleId,
            exampleType,
            letterRecommendationId,
            letterRecommendationTaskSuggestionIdx,
            recurrenceId,
            uniqueKey: state.uniqueKey,
            ...(clientStatus && {
              currentActiveTasks: clientStatus.currentActiveTasks,
              individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
              planMaxActiveTasks: clientStatus.planMaxActiveTasks,
            }),
          },
          event: 'createRecurrenceSuccessMessageViewed',
          topic: 'recurrences',
        });

        onCreateCallback?.({ ...data, isRecurrence: state.isRecurrence });
      },
      showErrorToast: true,
      showSuccessToast: false,
    });
  };

  const handleCreateOneOffTask = async (data: CreateTaskData) => {
    const {
      attachments = [],
      description,
      exampleId,
      exampleType,
      letterRecommendationId,
      letterRecommendationTaskSuggestionIdx,
      name,
      nextSteps,
      researchThreadId,
      researchThreadReferenceData,
      sharedWithClientIds,
    } = data;
    disconnectSocket();
    return handleMutation({
      mutation: async () => {
        analyticsTrack({
          data: {
            attachments,
            description,
            exampleId: exampleId,
            exampleType: exampleType,
            letterRecommendationId,
            letterRecommendationTaskSuggestionIdx,
            name,
            nextSteps,
            uniqueKey: state.uniqueKey,
            ...(clientStatus && {
              currentActiveTasks: clientStatus.currentActiveTasks,
              individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
              planMaxActiveTasks: clientStatus.planMaxActiveTasks,
            }),
          },
          event: 'createTaskSubmitButtonSelected',
          topic: 'tasks',
        });

        const formData = new TypedFormData<CreateTaskMutation['input']>();

        const basePayload: Omit<CreateTaskMutation['input']['data'], 'task_suggestion'> = {
          ai_questionnaire: answers.length
            ? { responses: formatAiQuestionnaireAnswersForSubmission(answers) }
            : undefined,
          description,
          example: exampleId,
          name,
          proceed_decision: nextSteps!,
          shared_with: sharedWithClientIds,
          task_source: platform,
        };

        const data = {
          ...basePayload,
          ...(researchThreadId
            ? {
                search_thread: {
                  id: researchThreadId,
                  message_idx: researchThreadReferenceData?.messageIdx,
                  task_suggestion_idx: researchThreadReferenceData?.taskSuggestionIdx,
                },
              }
            : {}),
          ...(letterRecommendationId && letterRecommendationTaskSuggestionIdx !== undefined
            ? {
                letter_recommendation: {
                  id: letterRecommendationId,
                  task_suggestion_idx: letterRecommendationTaskSuggestionIdx,
                },
              }
            : {}),
        };

        formData.appendStringified('data', data);

        formData.appendLocalFiles('attachments', attachments);
        return createTask({ formData }).unwrap();
      },
      onSuccess: (successData) => {
        const { id: taskId, status } = successData;
        onSuccessCreateOneOffTask(successData);
        analyticsTrack({
          data: {
            exampleId: exampleId,
            exampleType: exampleType,
            isDraft: status === 'draft',
            letterRecommendationId,
            letterRecommendationTaskSuggestionIdx,
            taskId,
            uniqueKey: state.uniqueKey,
            ...(clientStatus && {
              currentActiveTasks: clientStatus.currentActiveTasks,
              individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
              planMaxActiveTasks: clientStatus.planMaxActiveTasks,
            }),
          },
          event: 'createTaskSuccessMessageViewed',
          topic: 'tasks',
        });

        onCreateCallback?.({ ...data, isRecurrence: state.isRecurrence });
      },
      showSuccessToast: false,
    });
  };

  const handleUpdateDraftTask = async ({
    attachments = [],
    description,
    id,
    name,
    nextSteps,
  }: CreateTaskData & { id: string }) => {
    disconnectSocket();
    return handleMutation({
      mutation: async () => {
        analyticsTrack({
          data: {
            attachments,
            description,
            draftTaskId: id,
            name,
            nextSteps,
            uniqueKey: state.uniqueKey,
            ...(draftTask && { entityId: draftTask.id, entityType: 'draft' }),
            ...(clientStatus && {
              currentActiveTasks: clientStatus.currentActiveTasks,
              individualMaxActiveTasks: clientStatus.individualMaxActiveTasks,
              planMaxActiveTasks: clientStatus.planMaxActiveTasks,
            }),
          },
          event: 'updateDraftTaskSubmitButtonSelected',
          topic: 'draftTasks',
        });

        const formData = new TypedFormData<UpdateDraftTaskMutation['input']>();

        const updatePayload: UpdateDraftTaskMutation['input']['data'] = {
          ai_questionnaire: answers.length
            ? { responses: formatAiQuestionnaireAnswersForSubmission(answers) }
            : undefined,
          description,
          name,
          proceed_decision: nextSteps!,
        };

        formData.appendStringified('data', updatePayload);
        formData.appendLocalFiles('attachments', attachments);

        return updateDraftTask({ data: { formData }, id }).unwrap();
      },
      onSuccess: (successData) => {
        onSuccessUpdateDraftTask(successData);
        onCreateCallback?.({ ...state.taskData, isRecurrence: state.isRecurrence });
      },
      showErrorToast: true,
      showSuccessToast: false,
    });
  };

  const handleInitialQuestionsUpdate = debounce<SocketMethod<'taskCreation', 'update_initial_questions'>>(
    (json) => {
      const data = deserializeJson(json);
      setInitialQuestions(
        data.content.questions.map((q) => ({
          ...q,
          choices: q.choices,
        })),
      );
    },
    250,
    { maxWait: 500 },
  );

  const setupSocket = ({
    description,
    skipTitleGeneration = false,
    token,
  }: {
    description: string;
    skipTitleGeneration?: boolean;
    token: string;
  }) => {
    if (getSocket('taskCreation')) {
      disconnectSocket();
    }
    const socket = initializeSocket({
      channelType: 'taskCreation',
      token,
    });

    const onConnect = () => {
      skipTitleGeneration || socket.emit('generate_task_title', description);
    };

    const onDisconnect = () => {
      setIsStreaming(false);
    };

    const onCompleted = () => {
      disconnectSocket();
      setIsStreaming(false);
    };

    const onFailed = () => {
      setIsStreaming(false);
      disconnectSocket();
    };

    const handleSetTaskTitle: SocketMethod<'taskCreation', 'set_task_title'> = (data) => {
      const { content } = deserializeJson(data);
      updateTaskData({ name: content });
    };

    socket.on('update_initial_questions', handleInitialQuestionsUpdate);
    socket.on('set_task_title', handleSetTaskTitle);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('initial_questions_complete', onCompleted);
    socket.on('initial_questions_failed', onFailed);
    return socket;
  };

  const setStepByIndex = (index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setState((prev) => ({ ...prev, currentStep: state.steps[index]! }));
  };

  // No special data validation for completion, trusting that consumer correctly dispatches increment/decrement
  const decrementStep = () => {
    const currentIndex = state.steps.indexOf(state.currentStep);
    // Can't decrement if already at the first step
    if (currentIndex === 0) return closeModal();

    // if previous step is quota and we should skip
    const previousStepIsQuotaAndShouldSkip =
      (state.isRecurrence && state.steps[currentIndex - 1] === 'quota') ||
      (state.steps[currentIndex - 1] === 'quota' &&
        clientStatus &&
        skipQuotaStep(clientStatus, hideIndividualTaskQuotaStep));

    const quotaIsFirstStep = state.steps[0] === 'quota';

    if (previousStepIsQuotaAndShouldSkip && quotaIsFirstStep) return closeModal();

    setStepByIndex(currentIndex - (previousStepIsQuotaAndShouldSkip ? 2 : 1));
  };

  const generateInitialQuestions = ({
    isRecurrence,
    skipTitleGeneration = false,
    taskData,
    token,
  }: {
    isRecurrence: boolean;
    skipTitleGeneration?: boolean;
    taskData: CreateTaskData;
    token: MaybeUndefined<string>;
  }) => {
    const { attachments, description, interval, intervalCount, nextStartDate, researchThreadId } = taskData;
    setInitialQuestions([]);
    setAnswers([]);
    setDescriptionSnapshot(description);
    if (token) {
      setIsStreaming(true);
      const socket = setupSocket({ description, skipTitleGeneration, token });
      socket.emit(
        'generate_initial_questions',
        serializeJson({
          description: description,
          hasAttachment: !!attachments?.length,
          threadId: researchThreadId,
          ...(isRecurrence && {
            recurrenceDateNeeded: nextStartDate,
            recurrenceInterval: interval,
            recurrenceInterval_count: intervalCount,
          }),
        }),
      );
    }
  };

  const incrementStep = () => {
    const currentIndex = state.steps.indexOf(state.currentStep);
    const nextStep = currentIndex + 1 < state.steps.length ? state.steps[currentIndex + 1] : '';
    // if final step, submit instead
    if (
      state.currentStep === state.steps[state.steps.length - 1] ||
      (state.steps[currentIndex + 1] === 'initialQuestions' && !initialQuestions.length)
    ) {
      if (isCreateTaskLoading || isCreateRecurrenceLoading || isUpdateDraftTaskLoading) return; // prevent double submits
      if (draftTask) {
        handleUpdateDraftTask({ ...state.taskData, id: draftTask.id });
      } else {
        (state.isRecurrence ? handleCreateRecurringTask : handleCreateOneOffTask)(state.taskData);
      }
      return;
    }

    if (
      nextStep === 'nextSteps' &&
      state.steps.includes('initialQuestions') &&
      state.taskData.description !== descriptionSnapshot
    ) {
      setDescriptionSnapshot(state.taskData.description);
      generateInitialQuestions({
        isRecurrence: state.isRecurrence,
        skipTitleGeneration: shouldSkipTitleGeneration,
        taskData: state.taskData,
        token,
      });
    }

    // Can't increment if already at the last step
    if (currentIndex === state.steps.length - 1) return;
    setStepByIndex(currentIndex + 1);
  };

  /**
   *
   * @description
   * This function should be used by the consumer to open the task creation modal.
   * It prepares the state for the full task creation flow.
   */
  const startTaskCreation = async ({
    draftTask,
    initialData,
    isRecurrence = false,
    onCreateCallback,
    stepsToSkip = [],
  }: TaskCreationOptions) => {
    const key = generateUUID();

    // Sometimes this function gets triggered before the client status is fetched, so we need to wait for it
    const latestClientStatus = await (async () => {
      if (isUninitialized) {
        return retrieveClientStatus().unwrap();
      }
      return clientStatus;
    })();

    analyticsTrack({
      data: {
        uniqueKey: key,
        ...(latestClientStatus && {
          currentActiveTasks: latestClientStatus.currentActiveTasks,
          individualMaxActiveTasks: latestClientStatus.individualMaxActiveTasks,
          planMaxActiveTasks: latestClientStatus.planMaxActiveTasks,
        }),
        ...(draftTask && { entityId: draftTask.id, entityType: 'draft' }),
      },
      event: 'taskFlowStarted',
      topic: 'tasks',
    });

    if (!isUninitialized) {
      retrieveClientStatus();
    }

    // We're slicing here to remove the "init" step, which is only meant to prevent unintentionally
    // running any logic in the first step of the flow when the user hasn't summoned the modal, which happens
    // when we reset the "currentStep" back to the first step of the flow.
    const flowSteps = allSteps.slice(1).filter((step) => {
      // Filter out steps that are for repeating tasks only if it's not a recurrence
      if (stepsForRepeatingTasksOnly.includes(step) && !isRecurrence) return false;

      // Filter out steps that should be skipped
      if (stepsToSkip.includes(step as SkippableCreateTaskStep)) return false;

      // Filter out the 'quota' step if we are updating a draft task
      if (draftTask && step === 'quota') return false;

      return true;
    });

    if (draftTask) {
      setDraftTask(draftTask);
      if (draftTask?.aiQuestionnaire) {
        const { answers, initialQuestions } = formatDraftTaskAiQuestionnaire(draftTask.aiQuestionnaire);
        setInitialQuestions(initialQuestions);
        setAnswers(answers);
        setDescriptionSnapshot(draftTask.description);
      }
    }

    const taskData = {
      ...defaultTaskData,
      ...(draftTask
        ? {
            description: draftTask.description,
            name: draftTask.name,
            nextSteps: draftTask.proceedDecision as CreateTaskNextSteps,
          }
        : initialData),
    };

    if (onCreateCallback) {
      setOnCreateCallback(() => onCreateCallback);
    }

    setState({
      currentStep: flowSteps[0]!,
      isRecurrence,
      steps: flowSteps,
      taskData: taskData,
      uniqueKey: key,
    });
    setShouldSkipTitleGeneration(!!initialData?.name);
    if (flowSteps[0] === 'nextSteps' && flowSteps.includes('initialQuestions') && initialData) {
      generateInitialQuestions({
        isRecurrence: isRecurrence,
        skipTitleGeneration: !!initialData?.name,
        taskData: taskData,
        token,
      });
    }
    openModal();
  };

  return {
    answers,
    clearCreateTaskState,
    createTaskState: state,
    decrementStep,
    draftTask,
    incrementStep,
    initialQuestions,
    isOnLastStep: state.currentStep === state.steps[state.steps.length - 1],
    isStreaming,
    isSubmitting: isCreateRecurrenceLoading || isCreateTaskLoading || isUpdateDraftTaskLoading,
    onUpdateAnswer,
    startTaskCreation,
    updateTaskData,
  };
};
